import type { LeagueMemberRecord, SeasonPlayerRecord } from '../api/types.js';

export interface SeasonRosterAssignedEntry {
  playerId: string;
  name: string;
  email: string;
  role: LeagueMemberRecord['role'];
  source: 'existing' | 'pending-add';
  pendingRemoval: boolean;
  startingHandicap: number | null;
  currentHandicapIndex: number | null;
}

export interface SeasonRosterAvailableEntry {
  playerId: string;
  name: string;
  email: string;
  role: LeagueMemberRecord['role'];
}

export interface SeasonRosterWorkspaceView {
  assigned: SeasonRosterAssignedEntry[];
  available: SeasonRosterAvailableEntry[];
  pendingChangeCount: number;
}

interface BuildSeasonRosterWorkspaceInput {
  members: LeagueMemberRecord[];
  seasonPlayers: SeasonPlayerRecord[];
  searchTerm: string;
  pendingAdds: Record<string, true>;
  pendingRemovals: Record<string, true>;
}

function normalizeSearchTerm(value: string): string {
  return value.trim().toLowerCase();
}

function memberName(member: LeagueMemberRecord): string {
  return member.player?.name?.trim() || member.player_id;
}

function memberEmail(member: LeagueMemberRecord): string {
  return member.player?.email?.trim() || '';
}

function matchesSearch(searchTerm: string, values: string[]): boolean {
  if (!searchTerm) {
    return true;
  }

  return values.some((value) => value.toLowerCase().includes(searchTerm));
}

function sortByNameThenEmail<T extends { name: string; email: string }>(left: T, right: T): number {
  return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }) || left.email.localeCompare(right.email, undefined, { sensitivity: 'base' });
}

export function buildSeasonRosterWorkspace({
  members,
  seasonPlayers,
  searchTerm,
  pendingAdds,
  pendingRemovals,
}: BuildSeasonRosterWorkspaceInput): SeasonRosterWorkspaceView {
  const normalizedSearch = normalizeSearchTerm(searchTerm);
  const memberByPlayerId = new Map(members.map((member) => [member.player_id, member]));
  const seasonPlayerByPlayerId = new Map(
    seasonPlayers.map((seasonPlayer) => [seasonPlayer.player_id, seasonPlayer]),
  );
  const pendingAddIds = new Set(Object.keys(pendingAdds));
  const effectivePendingRemovalIds = new Set(
    Object.keys(pendingRemovals).filter((playerId) => seasonPlayerByPlayerId.has(playerId)),
  );

  const assigned: SeasonRosterAssignedEntry[] = [];
  const available: SeasonRosterAvailableEntry[] = [];

  for (const seasonPlayer of seasonPlayers) {
    const member = memberByPlayerId.get(seasonPlayer.player_id);
    const name = member ? memberName(member) : seasonPlayer.player?.name?.trim() || seasonPlayer.player_id;
    const email = member ? memberEmail(member) : seasonPlayer.player?.email?.trim() || '';
    const role = member?.role ?? 'player';
    const pendingRemoval = effectivePendingRemovalIds.has(seasonPlayer.player_id);

    const entry: SeasonRosterAssignedEntry = {
      playerId: seasonPlayer.player_id,
      name,
      email,
      role,
      source: 'existing',
      pendingRemoval,
      startingHandicap: member?.startingHandicap ?? seasonPlayer.starting_handicap,
      currentHandicapIndex: member?.currentHandicapIndex ?? seasonPlayer.current_handicap_index,
    };

    if (matchesSearch(normalizedSearch, [name, email, role])) {
      assigned.push(entry);
    }
  }

  for (const playerId of pendingAddIds) {
    const member = memberByPlayerId.get(playerId);
    if (!member || seasonPlayerByPlayerId.has(playerId)) {
      continue;
    }

    const entry: SeasonRosterAssignedEntry = {
      playerId,
      name: memberName(member),
      email: memberEmail(member),
      role: member.role,
      source: 'pending-add',
      pendingRemoval: false,
      startingHandicap: member.startingHandicap ?? null,
      currentHandicapIndex: member.currentHandicapIndex ?? null,
    };

    if (matchesSearch(normalizedSearch, [entry.name, entry.email, entry.role])) {
      assigned.push(entry);
    }
  }

  for (const member of members) {
    if (seasonPlayerByPlayerId.has(member.player_id) || pendingAddIds.has(member.player_id)) {
      continue;
    }

    const entry: SeasonRosterAvailableEntry = {
      playerId: member.player_id,
      name: memberName(member),
      email: memberEmail(member),
      role: member.role,
    };

    if (matchesSearch(normalizedSearch, [entry.name, entry.email, entry.role])) {
      available.push(entry);
    }
  }

  assigned.sort(sortByNameThenEmail);
  available.sort(sortByNameThenEmail);

  return {
    assigned,
    available,
    pendingChangeCount:
      Object.keys(pendingAdds).filter((playerId) => !seasonPlayerByPlayerId.has(playerId)).length +
      effectivePendingRemovalIds.size,
  };
}
